import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import logger from '../../utils/logger';
import moment from 'moment';
import BreadCrumb from '../BreadCrumb';
import {
  ClipboardDocumentCheckIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  TruckIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const GRNDetail = () => {
  const { id } = useParams();
  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);

  const pages = [
    { name: 'Procurement', to: '/procurement', component: Link, current: false },
    { name: 'Goods Received Notes', to: '/procurement/grn', component: Link, current: false },
    { name: 'GRN Details', to: '#', component: 'span', current: true },
  ];

  useEffect(() => {
    fetchGRN();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchGRN = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'goodsReceivedNotes', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setGrn({ id: docSnap.id, ...docSnap.data() });
      } else {
        toast.error('GRN not found');
      }
    } catch (error) {
      logger.error('Error fetching GRN:', error);
      toast.error('Failed to load GRN');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Fully Received': 'bg-emerald-100 text-emerald-800',
      'Partially Received': 'bg-amber-100 text-amber-800',
      'Discrepancy': 'bg-red-100 text-red-800',
      'Quality Issue': 'bg-orange-100 text-orange-800',
      'Accepted': 'bg-blue-100 text-blue-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getVarianceColor = (ordered, received) => {
    if (received < ordered) return 'text-red-600';
    if (received > ordered) return 'text-blue-600';
    return 'text-emerald-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="text-center py-12">
          <ClipboardDocumentCheckIcon className="h-16 w-16 text-white mx-auto mb-4" />
          <p className="text-white font-medium">GRN not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
      <BreadCrumb pages={pages} />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900">{grn.grnNumber}</h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  Received on{' '}
                  {grn.receivedDate?.toDate
                    ? moment(grn.receivedDate.toDate()).format('MMMM DD, YYYY')
                    : 'N/A'}
                </p>
              </div>
            </div>
            <span
              className={`px-4 py-2 inline-flex text-sm font-black rounded-full ${getStatusBadge(
                grn.status
              )}`}
            >
              {grn.status}
            </span>
          </div>

          {/* Warning Messages */}
          {grn.hasDiscrepancy && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-yellow-800 font-bold text-xs">⚠ Quantity Discrepancy Detected</p>
              <p className="text-yellow-700 text-xs mt-1">
                Some items have quantity differences from the purchase order
              </p>
            </div>
          )}

          {grn.hasQualityIssue && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
              <p className="text-red-800 font-bold text-xs">✗ Quality Issues Found</p>
              <p className="text-red-700 text-xs mt-1">
                Some items failed quality inspection
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            {grn.poId && (
              <Link
                to={`/procurement/purchase-orders/${grn.poId}`}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                View Purchase Order
              </Link>
            )}
            <Link
              to="/procurement/grn"
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-all duration-200"
            >
              Back to List
            </Link>
          </div>
        </div>

        {/* Details Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Supplier Information */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
              <BuildingOfficeIcon className="h-5 w-5 text-white" />
              <h2 className="text-sm font-black text-white">Supplier Information</h2>
            </div>
            <dl className="space-y-2 mt-4">
              <div>
                <dt className="text-xs font-medium text-gray-600">Company Name</dt>
                <dd className="text-xs font-bold text-gray-900 mt-0.5">{grn.supplier || 'N/A'}</dd>
              </div>
              {grn.supplierAddress && (
                <div>
                  <dt className="text-xs font-medium text-gray-600">Address</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{grn.supplierAddress}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Receipt Information */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
              <TruckIcon className="h-5 w-5 text-white" />
              <h2 className="text-sm font-black text-white">Receipt Information</h2>
            </div>
            <dl className="space-y-2 mt-4">
              {grn.poNumber && (
                <div>
                  <dt className="text-xs font-medium text-gray-600">PO Number</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{grn.poNumber}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-gray-600">Received By</dt>
                <dd className="text-xs font-bold text-gray-900 mt-0.5">{grn.receivedBy || 'N/A'}</dd>
              </div>
              {grn.deliveryNote && (
                <div>
                  <dt className="text-xs font-medium text-gray-600">Delivery Note</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{grn.deliveryNote}</dd>
                </div>
              )}
              {grn.vehicleNumber && (
                <div>
                  <dt className="text-xs font-medium text-gray-600">Vehicle Number</dt>
                  <dd className="text-xs font-bold text-gray-900 mt-0.5">{grn.vehicleNumber}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600 -m-4 p-4 rounded-t-xl">
              <BanknotesIcon className="h-5 w-5 text-white" />
              <h2 className="text-sm font-black text-white">Receipt Summary</h2>
            </div>
            <dl className="space-y-2 mt-4">
              <div className="flex justify-between">
                <dt className="text-xs font-medium text-gray-600">Total Ordered</dt>
                <dd className="text-xs font-black text-gray-900">
                  {grn.items?.reduce((sum, item) => sum + item.orderedQuantity, 0) || 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-xs font-medium text-gray-600">Total Received</dt>
                <dd className="text-xs font-black text-gray-900">
                  {grn.items?.reduce((sum, item) => sum + item.receivedQuantity, 0) || 0}
                </dd>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <dt className="text-xs font-medium text-emerald-700">Accepted</dt>
                <dd className="text-xs font-black text-emerald-900">
                  {grn.items?.reduce((sum, item) => sum + item.acceptedQuantity, 0) || 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-xs font-medium text-red-700">Rejected</dt>
                <dd className="text-xs font-black text-red-900">
                  {grn.items?.reduce((sum, item) => sum + item.rejectedQuantity, 0) || 0}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4">
          <div className="flex items-center space-x-2 p-4 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600">
            <ClipboardDocumentCheckIcon className="h-5 w-5 text-white" />
            <h2 className="text-sm font-black text-white">Items Received</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase">#</th>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase">
                    Description
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-gray-700 uppercase">
                    Ordered
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-gray-700 uppercase">
                    Received
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-gray-700 uppercase">
                    Accepted
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-gray-700 uppercase">
                    Rejected
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-gray-700 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(grn.items || []).map((item, index) => (
                  <tr key={index} className={!item.inspectionPassed ? 'bg-red-50' : 'hover:bg-green-50 transition-colors duration-150'}>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-bold text-gray-900">{index + 1}</td>
                    <td className="px-2 py-1.5 text-xs text-gray-900">
                      <div className="font-bold">{item.description}</div>
                      {item.qualityIssue && (
                        <div className="text-xs text-red-600 mt-1">Issue: {item.qualityIssue}</div>
                      )}
                      {item.damageType && (
                        <div className="text-xs text-orange-600 mt-1">Damage: {item.damageType}</div>
                      )}
                      {item.remarks && (
                        <div className="text-xs text-gray-500 mt-1">Note: {item.remarks}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs text-center font-bold text-gray-900">
                      {item.orderedQuantity}
                    </td>
                    <td className={`px-2 py-1.5 whitespace-nowrap text-xs text-center font-black ${getVarianceColor(item.orderedQuantity, item.receivedQuantity)}`}>
                      {item.receivedQuantity}
                      {item.receivedQuantity !== item.orderedQuantity && (
                        <span className="ml-1">
                          {item.receivedQuantity < item.orderedQuantity ? '▼' : '▲'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs text-center font-black text-emerald-900">
                      {item.acceptedQuantity}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs text-center font-black text-red-900">
                      {item.rejectedQuantity}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-center">
                      {item.inspectionPassed ? (
                        <span className="px-2 py-1 text-xs font-black rounded-full bg-emerald-100 text-emerald-800">
                          ✓ Pass
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-black rounded-full bg-red-100 text-red-800">
                          ✗ Fail
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Photos */}
        {grn.photos && grn.photos.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
            <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200">
              <PhotoIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-sm font-black text-gray-900">Inspection Photos</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {grn.photos.map((photo, index) => (
                <div key={index} className="relative">
                  <img
                    src={photo.url}
                    alt={`Inspection ${index + 1}`}
                    className="w-full h-40 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200"
                  />
                  <div className="text-xs text-gray-600 font-medium mt-1">{photo.name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Remarks */}
        {grn.remarks && (
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h2 className="text-sm font-black text-gray-900 mb-3">General Remarks</h2>
            <p className="text-xs text-gray-700 leading-relaxed">{grn.remarks}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GRNDetail;
